import { HttpClientError, HttpClientRequest, HttpClientResponse } from 'effect/unstable/http'
import {
  HttpStatusError,
  InvalidRequestError,
  InvalidResponseError,
  TimeoutError,
  TransportError,
  inferFetchFailure,
  type RequestTrace,
  type ResponseTrace
} from '../fetch-errors'

export function mapHttpClientError(
  error: HttpClientError.HttpClientError
): TransportError | TimeoutError | InvalidRequestError | InvalidResponseError | HttpStatusError {
  const requestTrace = requestTraceFromHttpRequest(error.request)
  const redactedRequest = redactRequest(requestTrace)
  const reason = error.reason

  switch (reason._tag) {
    case 'InvalidUrlError':
      return new InvalidRequestError(redactedRequest, 'invalid_url', {
        cause: reason.cause,
        description: reason.description
      })
    case 'EncodeError':
      return new InvalidRequestError(redactedRequest, 'encode_error', {
        cause: reason.cause,
        description: reason.description
      })
    case 'TransportError': {
      const classification = inferFetchFailure({
        request: { url: requestTrace.url, method: requestTrace.method },
        error: reason.cause ?? reason
      })

      if (classification.type == 'timeout') {
        return new TimeoutError(redactedRequest, {
          cause: reason.cause,
          description: reason.description,
          timeoutMs: classification.timeoutMs
        })
      }

      if (classification.type == 'invalidRequest') {
        return new InvalidRequestError(redactedRequest, 'invalid_url', {
          cause: reason.cause,
          description: reason.description
        })
      }

      return new TransportError(redactedRequest, {
        cause: reason.cause,
        description: reason.description,
        classification
      })
    }
    case 'StatusCodeError': {
      const responseTrace = redactResponse({
        status: reason.response.status,
        headers: toHeaderRecord(reason.response.headers)
      })

      return new HttpStatusError(redactedRequest, responseTrace, {
        cause: reason.cause,
        description: reason.description
      })
    }
    case 'DecodeError':
    case 'EmptyBodyError':
      return new InvalidResponseError(
        redactedRequest,
        reason._tag == 'DecodeError' ? 'invalid_json' : 'read_error',
        {
          response: redactResponse({
            status: reason.response.status,
            headers: toHeaderRecord(reason.response.headers)
          }),
          cause: reason.cause,
          description: reason.description
        }
      )
  }
}

export function createHttpStatusError(
  request: RequestTrace,
  response: { status: number; headers: Record<string, string>; body?: unknown },
  opts?: {
    cause?: unknown
    description?: string
  }
): HttpStatusError {
  const redacted = redactRequestTrace({
    request,
    response: {
      status: response.status,
      headers: response.headers,
      body: response.body
    }
  })

  return new HttpStatusError(redacted.request, redacted.response, opts)
}

export function createInvalidJsonResponseError(
  request: RequestTrace,
  response: HttpClientResponse.HttpClientResponse
): (error: unknown) => InvalidResponseError {
  return (error: unknown) =>
    new InvalidResponseError(redactRequest(request), 'invalid_json', {
      cause: error,
      response: redactResponse({
        status: response.status,
        headers: toHeaderRecord(response.headers)
      })
    })
}

export function responseHeadersToRecord(response: HttpClientResponse.HttpClientResponse) {
  return toHeaderRecord(response.headers)
}

export function requestTraceFromHttpRequest(
  request: HttpClientRequest.HttpClientRequest
): RequestTrace {
  return {
    url: request.url,
    method: request.method,
    headers: { ...request.headers }
  }
}

type InternalRequestTrace = {
  request: RequestTrace
  response: ResponseTrace
}

function redactRequestTrace(trace: InternalRequestTrace): InternalRequestTrace {
  return {
    request: redactRequest(trace.request),
    response: redactResponse(trace.response)
  }
}

function redactRequest(request: RequestTrace): RequestTrace {
  return {
    ...request,
    headers: redactHeaders(request.headers, /auth|cookie|token|key|secret/i)
  }
}

function redactResponse(response: ResponseTrace): ResponseTrace {
  return {
    ...response,
    headers: redactHeaders(response.headers, /auth|cookie|token|key|secret/i)
  }
}

function redactHeaders(
  headers: Record<string, string> | undefined,
  regex: RegExp
): Record<string, string> | undefined {
  if (!headers) {
    return headers
  }

  const redacted = { ...headers }
  for (const key in redacted) {
    if (regex.test(key)) {
      redacted[key] = redactAuth(redacted[key])
    }
  }
  return redacted
}

function toHeaderRecord(headers: Record<string, string>): Record<string, string> {
  const record: Record<string, string> = {}
  for (const [key, value] of Object.entries(headers)) {
    if (typeof value == 'string') {
      record[key] = value
    }
  }
  return record
}

function redactAuth(value: string): string {
  if (!value) {
    return value
  }

  return '<redacted>'
}
